import React, { useEffect, useState } from 'react';
import './MassageBox.css';
import { ServerAddress } from '../../../constants/ServerAddress';
import { useSelector } from 'react-redux';
import axios from 'axios';
import {
  FaFilePdf,
  FaFileWord,
  FaFileExcel,
  FaFileImage,
  FaFileAlt,
  FaChevronDown,
  FaChevronUp,
  FaCalendarAlt,
  FaDownload
} from 'react-icons/fa';
import { Modal } from 'react-bootstrap';
import moment from 'moment';
import DateConvertor from '../../../components/dateConvertor/DateConvertor';
import toTitleCase from '../../../lib/titleCase/TitleCase';

const MessageBox = () => {
  const accessToken = useSelector((state) => state.authentication.access_token);
  const [notices, setNotices] = useState([]);
  const [selectedAttachment, setSelectedAttachment] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const fetchNotices = async () => {
    try {
      const response = await axios.get(
        ServerAddress + `notice_board/get-Notice/?&p=${1}&records=${100}`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      const sortedData = response.data.results?.sort((a, b) =>
        new Date(b.date_of_expiry) - new Date(a.date_of_expiry)
      );
      setNotices(sortedData || []);
    } catch (err) {
      console.error('Error fetching notices:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (accessToken) fetchNotices();
  }, [accessToken]);

  const getFileIcon = (fileUrl) => {
    if (!fileUrl) return null;
    const fileExtension = fileUrl.split('.').pop().toLowerCase();

    switch (fileExtension) {
      case 'pdf': return <FaFilePdf className="file-icon pdf" />;
      case 'doc': case 'docx': return <FaFileWord className="file-icon word" />;
      case 'xls': case 'xlsx': return <FaFileExcel className="file-icon excel" />;
      case 'jpg': case 'jpeg': case 'png': case 'gif': case 'webp':
        return <FaFileImage className="file-icon image" />;
      default: return <FaFileAlt className="file-icon generic" />;
    }
  };

  const handleAttachmentClick = (fileUrl) => {
    setSelectedAttachment(fileUrl);
    setShowModal(true);
  };

  const displayedNotices = showAll ? notices : notices.slice(0, 5);

  return (
    <div className="message-container">
      <div className="message-header">
        <div className="header-content">
          <h4 style={{ color: 'white' }}>Notice Board</h4>
          <div className="header-meta">
            <span className="notice-count">{notices.length} notices</span>
          </div>
        </div>
      </div>

      <div className="message-content">
        {isLoading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <span>Loading notices...</span>
          </div>
        ) : notices.length === 0 ? (
          <div className="empty-state">
            <p>No notices available</p>
            <small>New notices will appear here when posted</small>
          </div>
        ) : (
          <>
            <div className="notice-list">
              {displayedNotices.map((notice) => (
                <div key={notice.id} className="notice-card">
                  <div className="notice-main">
                    <div className="notice-meta">
                      <div className="notice-sender">
                        <span>
                          <i
                            className={notice.sender === 'manager' ? 'fas fa-user-tie' : 'fas fa-user-circle'}
                            style={{ marginRight: '5px' }}
                          ></i>
                        </span>
                        <span>{notice.user_name}</span>
                      </div>
                      <div className="notice-expiry">
                        <FaCalendarAlt /> Exp: {<DateConvertor inputDate={notice.date_of_expiry} />}
                      </div>
                    </div>
                    <div className="notice-title">{toTitleCase(notice.title)}</div>
                  </div>
                  <div className="attachment-preview-container">{notice.description}</div>
                  {notice?.attachment?.[0]?.attachment_file && (
                    <div className="attachment-preview-container">
                      <div
                        className="attachment-preview"
                        onClick={() => handleAttachmentClick(notice.attachment[0].attachment_file)}
                      >
                        {['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(
                          notice.attachment[0].attachment_file.split('.').pop().toLowerCase()) ? (
                          <img
                            src={notice.attachment[0].attachment_file}
                            alt="Notice attachment preview"
                            className="image-preview"
                          />
                        ) : (
                          <div className="file-preview">
                            {getFileIcon(notice.attachment[0].attachment_file)}
                            <span className="file-name">
                              {notice.attachment[0].attachment_file.split('/').pop()}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {notices.length > 5 && (
              <button
                className="view-toggle-btn"
                onClick={() => setShowAll(!showAll)}
              >
                {showAll ? (
                  <>
                    <FaChevronUp /> Show Less
                  </>
                ) : (
                  <>
                    <FaChevronDown /> View All ({notices.length - 5} more)
                  </>
                )}
              </button>
            )}
          </>
        )}
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Attachment Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body className="attachment-modal-body">
          {selectedAttachment ? (
            <div className="attachment-preview">
              {selectedAttachment.split('.').pop().toLowerCase() === 'pdf' ? (
                <embed
                  src={selectedAttachment}
                  type="application/pdf"
                  width="100%"
                  height="500px"
                />
              ) : ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(
                selectedAttachment.split('.').pop().toLowerCase()) ? (
                <div className="image-preview-container">
                  <img
                    src={selectedAttachment}
                    alt="Notice attachment"
                    className="modal-image-preview"
                  />
                </div>
              ) : (
                <div className="unsupported-preview">
                  <div className="file-icon-large">
                    {getFileIcon(selectedAttachment)}
                  </div>
                  <p className="file-name-modal">
                    {selectedAttachment.split('/').pop()}
                  </p>
                  <a
                    href={selectedAttachment}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="download-btn"
                  >
                    <FaDownload /> Download File
                  </a>
                </div>
              )}
            </div>
          ) : (
            <div className="no-attachment">No attachment available</div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default MessageBox;